import { Dayjs } from 'dayjs';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { TimeZone } from '../../../time/timezone.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { CustomerProfile } from '../customer-profile.js';
import { LocalizedString } from '../localized-string.js';
import { cloneDataOrNull, isSameData } from '../../dto/data-model-utils.js';
import { Duration } from 'dayjs/plugin/duration.js';
import { VersionRange } from '../../core/version-range.js';

export class GymPassPresence extends DataModel {
  static readonly TYPE_ID: string = 'c6cd741873774b00affc9b6c13cabc92';
  static readonly SCHEMA_VERSION: number = 2;

  public readonly expirationDate: Dayjs | null;

  private constructor(
    public readonly uniqueId: string,
    public readonly creationDate: Dayjs,
    public readonly entryTime: Dayjs,
    public readonly passId: string,
    public readonly passName: LocalizedString,
    public readonly customer: CustomerProfile | null,
    public readonly passExpirationDate: Dayjs | null,
    public readonly passUses: number | null,
    public readonly scanValidity: Duration | null,
    public readonly isInside: boolean,
  ) {
    super();
    this.expirationDate = scanValidity != null ? entryTime.add(scanValidity) : null;
  }

  get $typeId(): string {
    return GymPassPresence.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, GymPassPresence.SCHEMA_VERSION);
  }

  static inside(opts: {
    uniqueId: string,
    creationDate: Dayjs,
    entryTime: Dayjs,
    passId: string,
    passName: LocalizedString,
    customer: CustomerProfile | null,
    passExpirationDate: Dayjs | null,
    passUses: number | null,
    scanValidity: Duration | null,
  }) {
    return new GymPassPresence(
        opts.uniqueId,
        opts.creationDate,
        opts.entryTime,
        opts.passId,
        opts.passName,
        opts.customer,
        opts.passExpirationDate,
        opts.passUses,
        opts.scanValidity,
        true,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): GymPassPresence {
    return new GymPassPresence(
        MapUtils.get<string>(map, 'uniqueId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        context.decodeDate(MapUtils.get<number>(map, 'entryTime')),
        MapUtils.get<string>(map, 'passId'),
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'passName')),
        context.fromDataOrNull(MapUtils.getOrNull<SerializedData>(map, 'customer')),
        context.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'passExpirationDate')),
        MapUtils.getOrNull<number>(map, 'passUses'),
        context.decodeDurationOrNull(MapUtils.getOrNull<number>(map, 'scanValidity')),
        MapUtils.get<boolean>(map, 'isInside'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creationDate': context.encodeDate(this.creationDate),
      'entryTime': context.encodeDate(this.entryTime),
      'passId': this.passId,
      'passName': context.toData(this.passName),
      'customer': context.toDataOrNull(this.customer),
      'passExpirationDate': context.encodeDateOrNull(this.passExpirationDate),
      'passUses': this.passUses,
      'scanValidity': context.encodeDurationOrNull(this.scanValidity),
      'isInside': this.isInside,
    };
  }

  clone(): this {
    return new GymPassPresence(
        this.uniqueId,
        TimeZone.cloneDate(this.creationDate),
        TimeZone.cloneDate(this.entryTime),
        this.passId,
        this.passName.clone(),
        cloneDataOrNull(this.customer),
        TimeZone.cloneDateOrNull(this.passExpirationDate),
        this.passUses,
        TimeZone.cloneDurationOrNull(this.scanValidity),
        this.isInside,
    ) as this;
  }

  isSame(other: GymPassPresence): boolean {
    if(other === this) {
      return true;
    }

    return other.creationDate == this.creationDate &&
        other.entryTime == this.entryTime &&
        isSameData(other.passName, this.passName) &&
        isSameData(other.customer, this.customer) &&
        other.passExpirationDate === this.passExpirationDate &&
        other.passUses === this.passUses &&
        other.scanValidity == this.scanValidity &&
        other.passId === this.passId &&
        other.uniqueId === this.uniqueId &&
        other.isInside === this.isInside;
  }

  copyWithState(isInside: boolean): this {
    return new GymPassPresence(
        this.uniqueId,
        TimeZone.cloneDate(this.creationDate),
        TimeZone.cloneDate(this.entryTime),
        this.passId,
        this.passName.clone(),
        cloneDataOrNull(this.customer),
        TimeZone.cloneDateOrNull(this.passExpirationDate),
        this.passUses,
        TimeZone.cloneDurationOrNull(this.scanValidity),
        isInside,
    ) as this;
  }
}

export class GymPassPresenceBuilder extends DataModelBuilder<GymPassPresence> {
  build(context: SerializationContext, map: SerializedData): GymPassPresence {
    return GymPassPresence.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(GymPassPresence.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(GymPassPresence.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the following parameters were introduced:
    // - passExpirationDate: Optional<DateTime> (expiration date of the pass *)
    // - passUses: Optional<int> (number of uses left *)
    // * valid pass data when this presence was created
    if(version < 2) {
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
